<script lang="ts" setup>
import {
  CountDown,
  Field,
  Form,
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showNotify,
} from "vant";
import i18n, { getLocaleIdByLocale } from "@/locales";
import api from "@/modules/api";
import { ResponseError } from "@/types/api-types";
import { computed, onMounted, reactive, ref } from "vue";
import { useGlobalUser } from "@/stores/globalUser";
import { useRouteQuery } from "@vueuse/router";
import { useRoute, useRouter } from "vue-router";
import { watch } from "vue";
import { CreateOneUserReq } from "@/types/api-types";

const isCN = !!import.meta.env.VITE_IS_CN;
const EMAIL_REG: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REG: RegExp = /^1[3-9]\d{9}$/;
// Props & Emits & Constants
withDefaults(
  defineProps<{
    theme: "dark" | "light";
  }>(),
  {
    theme: "dark",
  }
);
const emit = defineEmits<{
  (e: "loginSuccess"): void;
}>();
const RESEND_INTERVAL_MS = 60;
const globalUser = useGlobalUser();
const router = useRouter();
const route = useRoute();
const mode = useRouteQuery<"login" | "register">("mode", "login");
const invitationCode = useRouteQuery("code", globalUser.invitationCode);
const agree = ref(false);
// watch(
//   mode,
//   (newMode) => {
//     globalUser.loginModal.type = newMode;
//   },
//   { immediate: true }
// );

watch(
  () => globalUser.loginModal.show,
  (newShow) => {
    if (newShow) {
      if (route.name === "mateCreate") {
        mode.value = "register";
      }
    }
  }
);

onMounted(() => {
  if (invitationCode.value) {
    globalUser.invitationCode = invitationCode.value;
  }
  if (route.name === "mateCreate") {
    mode.value = "register";
  }
});
// State
const state = reactive({
  isStaticValidation: false,
  email: "",
  phone: "",
  showCodeInput: true, // 是否显示验证码输入框
  code: "", // 验证码
  nextResendTime: 0, // 下次可重新发送验证码的时间
  isSendingCode: false, // 发送验证码按钮的loading状态
  isSubmitting: false, // 提交按钮的loading状态
  inviteCode: invitationCode.value,
});
const formRef = ref<HTMLFormElement | null>(null);
const isResendAvailable = computed(() => {
  return state.nextResendTime - Date.now() < 0;
});

// Methods

async function appleLogin() {
  window.open(
    import.meta.env.VITE_API_URL + "/v1/Auth/Apple/Form",
    "popup",
    "width=600,height=600,centerscreen"
  );
  showConfirmDialog({
    title: i18n.global.t("ConfirmModal.googleLoginTitle"),
    message: i18n.global.t("ConfirmModal.googleLoginInfo"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  })
    .then(async () => {
      const loading = showLoadingToast(i18n.global.t("Message.checkingTicket"));
      try {
        await globalUser.init();
        gtag("event", "conversion_event_signup");
        emit("loginSuccess");
      } catch {
        loading.close();
        showNotify(i18n.global.t("Message.notLogin"));
      } finally {
        loading.close();
      }
    })
    .catch(() => {});
}

async function googleLogin() {
  window.open(
    import.meta.env.VITE_API_URL + "/v1/Auth/Google/Form",
    "popup",
    "width=600,height=600,centerscreen"
  );
  showConfirmDialog({
    title: i18n.global.t("ConfirmModal.googleLoginTitle"),
    message: i18n.global.t("ConfirmModal.googleLoginInfo"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  })
    .then(async () => {
      const loading = showLoadingToast(i18n.global.t("Message.checkingTicket"));
      try {
        await globalUser.init();
        gtag("event", "conversion_event_signup");
        emit("loginSuccess");
      } catch {
        loading.close();
        showNotify(i18n.global.t("Message.notLogin"));
      } finally {
        loading.close();
      }
    })
    .catch(() => {});
}

const onSubmit = async () => {
  if (!agree.value) return (showAgreeAndContinue.value = true);

  // case1. 发送验证码并展示验证码输入框
  if (state.email.match(/^.*@anymate.com$/) && !state.showCodeInput) {
    state.showCodeInput = true;
    state.isStaticValidation = true;
  } else if (!state.showCodeInput) {
    await sendVerificationCode();
  } else {
    // case2.提交验证码
    if (state.code.length < 4) {
      showFailToast(i18n.global.t("Message.pleaseEnterVerificationCode"));
      return;
    }
    const defaultLocale = getLocaleIdByLocale(i18n.global.locale.value);
    state.isSubmitting = true;
    try {
      let params: Partial<CreateOneUserReq> = {
        token: Number(state.code), // 验证码
        settingDisplayLanguageId: defaultLocale,
        settingMateLanguageId: defaultLocale,
        invitationCode:
          mode.value === "register" ? state.inviteCode : undefined,
      };

      if (isPhoneLogin.value) {
        params.phone = state.phone;
        params.name = state.phone.slice(0, 3) + "****" + state.phone.slice(-4);
      } else {
        params.email = state.email;
        params.name = state.email.split("@")[0];
      }

      const res = await api.createOneUser(params as CreateOneUserReq);
      await globalUser.login(res.item);
      gtag("event", "conversion_event_signup");
      emit("loginSuccess");
    } finally {
      state.isSubmitting = false;
    }
  }
};

const sendVerificationCode = async () => {
  state.isSendingCode = true;
  try {
    if (!isPhoneLogin.value) {
      await api.createOneVerification({ email: state.email });
    } else {
      await api.createOneVerification({ phone: state.phone });
    }
    state.nextResendTime = Date.now() + RESEND_INTERVAL_MS * 1000;
    state.showCodeInput = true;
  } catch (e) {
    // showFailToast((e as ResponseError).message);
    showFailToast(`请求失败啦，重试一下呢`);
    throw e;
  } finally {
    state.isSendingCode = false;
  }
};

const checkUserExist = async (): Promise<boolean> => {
  const params = {
    email: isPhoneLogin.value ? undefined : state.email,
    phone: isPhoneLogin.value ? state.phone : undefined,
  };
  const res = await api.checkUserExist(params);
  return res.data.exists;
};

const sendCodeAfterCheckUserExist = async () => {
  // 1. 若为登录，则检查用户是否存在，若不存在，则跳转至注册页面
  if (mode.value === "login") {
    const exists = await checkUserExist();
    if (!exists) {
      showFailToast(i18n.global.t("Message.registerFirst"));
      router.push({
        name: "mateCreate",
      });
      globalUser.loginModal.show = false;
      return;
    }
  }
  await sendVerificationCode();
};
const isPhoneLogin = ref(true);

const showPolicy = ref(false);
const showAgreeAndContinue = ref(false);
const policyType = ref("");
const clickPolicy = (type: string) => {
  policyType.value = type;
  showPolicy.value = true;
};

const clickAgreeAndContinue = () => {
  showAgreeAndContinue.value = false;
  agree.value = true;
  onSubmit();
};

const clickSwitchMode = () => {
  isPhoneLogin.value = true;
  if (mode.value === "login") {
    if (route.name !== "mateCreate") {
      router.replace({
        name: "mateCreate",
      });
      globalUser.loginModal.show = false;
    } else {
      mode.value = "register";
    }
  } else {
    mode.value = "login";
  }
};

onMounted(() => {
  if (isCN) {
    // isPhoneLogin.value = true;
  }
});

const couldSendCode = computed(() => {
  if (state.isSendingCode) return false;
  if (isPhoneLogin.value) {
    return state.phone.match(PHONE_REG);
  } else {
    return state.email.match(EMAIL_REG);
  }
});
</script>
<template>
  <div class="flex flex-col gap-2">
    <button v-if="!isCN" class="btn w-full btn-primary" @click="googleLogin">
      <google-icon />
      {{ $t("loginmodal.googleLoginInfo") }}
    </button>
    <button
      v-if="!isCN"
      id="appleid-signin"
      class="btn w-full btn-primary"
      data-border="true"
      data-color="black"
      data-type="sign in"
      @click="appleLogin"
    >
      <i class="iconfont icon-apple-logo text-xl" />
      {{ $t("loginmodal.signInWithApple") }}
    </button>
    <Form
      ref="formRef"
      class="flex flex-col gap-2 flex-grow w-full"
      @submit="onSubmit"
    >
      <template v-if="!isPhoneLogin">
        <div class="flex justify-between items-center">
          <label class="text-sm xs:text-base">
            {{ $t("loginmodal.email") }}
          </label>
          <button
            v-if="isCN"
            class="btn btn-xs rounded"
            :disabled="state.isSendingCode || !isResendAvailable"
            @click="isPhoneLogin = true"
            type="button"
          >
            {{ $t("loginmodal.switchToPhone") + $t(`User.${mode}`) }}
          </button>
        </div>
        <field
          v-model="state.email"
          name="email"
          :class="{
            'theme-white': theme === 'light',
          }"
          :disabled="state.isSendingCode"
          :placeholder="$t('loginmodal.emailplaceholder')"
          :rules="[
            {
              required: true,
              message: $t('loginmodal.emailplaceholderCorrectly'),
              pattern: EMAIL_REG,
            },
          ]"
          class="bg-transparent border-none input-field login-input"
          type="email"
        />
      </template>
      <template v-else>
        <div class="flex justify-between items-center">
          <label class="text-sm xs:text-base">
            {{ $t("loginmodal.phone") }}
          </label>
          <button
            v-if="isCN"
            class="btn btn-xs rounded"
            :disabled="
              state.isSendingCode || state.nextResendTime - Date.now() > 0
            "
            @click="isPhoneLogin = false"
            type="button"
          >
            {{ $t("loginmodal.switchToEmail") + $t(`User.${mode}`) }}
          </button>
        </div>
        <field
          v-model="state.phone"
          name="phone"
          :class="{
            'theme-white': theme === 'light',
          }"
          :disabled="state.isSendingCode"
          :placeholder="$t('loginmodal.phoneplaceholder')"
          :rules="[
            {
              required: true,
              message: $t('loginmodal.phoneplaceholderCorrectly'),
              pattern: PHONE_REG,
            },
          ]"
          class="bg-transparent border-none input-field login-input"
          type="tel"
        />
      </template>
      <template v-if="mode === 'register'">
        <!-- 邀请码 -->
        <label class="text-sm xs:text-base">{{
          $t("loginmodal.inviteCode")
        }}</label>
        <field
          v-model="state.inviteCode"
          :class="{
            'theme-white': theme === 'light',
          }"
          :disabled="state.isSendingCode"
          :placeholder="$t('loginmodal.inviteCodePlaceholder')"
          class="bg-transparent border-none input-field login-input"
          type="text"
        />
      </template>
      <div v-if="state.showCodeInput" class="flex flex-col gap-3">
        <div class="flex justify-between items-center">
          <label class="text-sm xs:text-base">{{
            state.isStaticValidation
              ? $t("loginmodal.staticCode")
              : $t("loginmodal.verificationcode")
          }}</label>
          <div v-if="!isResendAvailable" class="text-xs xs:text-sm">
            {{ $t("loginmodal.resendHit") }}
            <count-down
              :time="state.nextResendTime - Date.now()"
              class="inline-block text-xs xs:text-sm text-inherit"
              format="ss"
              @finish="state.nextResendTime = 0"
            />
            {{ $t("loginmodal.resendHit2") }}
          </div>
        </div>
        <div class="flex gap-2">
          <field
            v-model="state.code"
            :class="{
              'theme-white': theme === 'light',
            }"
            :disabled="state.isSendingCode"
            :placeholder="$t('loginmodal.inputVerificationCode')"
            :rules="[
              {
                required: true,
                message: $t('loginmodal.inputVerificationCode'),
              },
            ]"
            class="bg-transparent border-none input-field login-input"
            type="text"
          />
          <template v-if="!state.isStaticValidation">
            <button
              v-if="isResendAvailable"
              :disabled="!couldSendCode"
              class="btn bg-white text-black btn-sm xs:btn-md rounded"
              @click="sendCodeAfterCheckUserExist"
            >
              <span
                v-if="state.isSendingCode"
                class="loading loading-spinner"
              />
              {{ $t("loginmodal.sendcode") }}
            </button>
            <button
              v-else
              class="btn btn-sm xs:btn-md rounded"
              disabled
              type="button"
            >
              {{ $t("loginmodal.resend") }}
            </button>
          </template>
        </div>
      </div>
      <button
        :disabled="
          state.isSubmitting || (!state.showCodeInput && state.isSendingCode)
        "
        class="btn btn-primary rounded btn-sm xs:btn-md"
        type="submit"
      >
        <span
          v-if="
            state.isSubmitting || (!state.showCodeInput && state.isSendingCode)
          "
          class="loading loading-spinner"
        ></span>
        {{ $t("loginmodal.continue") }}
      </button>
      <button
        :class="[
          'btn btn-primary btn-ghost rounded btn-sm xs:btn-md',
          'underline',
        ]"
        type="button"
        @click="clickSwitchMode"
      >
        {{
          mode === "register"
            ? $t("loginmodal.switchToLogin")
            : $t("loginmodal.switchToRegister")
        }}
      </button>
    </Form>
    <!-- TODO: 国内用户协议 -->
    <div class="flex items-center gap-2" @click="agree = !agree">
      <div>
        <VanCheckbox :model-value="agree" shape="square" />
      </div>
      <p>
        {{
          $t(
            globalUser.loginModal.type === "login"
              ? "loginmodal.loginAgreement"
              : "loginmodal.registerAgreement"
          )
        }}
        <span
          @click="clickPolicy(isCN ? 'user-agreement-cn' : 'terms-of-service')"
          class="text-primary"
        >
          {{ $t("loginmodal.userAgreement") }}
        </span>
        {{ $t("loginmodal.and") }}
        <span
          @click="clickPolicy(isCN ? 'privacy-policy-cn' : 'privacy-policy')"
          class="text-primary"
        >
          {{ $t("loginmodal.privacyPolicy") }}
        </span>
      </p>
    </div>
  </div>

  <VanPopup v-model:show="showAgreeAndContinue" position="bottom" closeable>
    <div class="card bg-base-100 shadow-xl p-6 max-w-md mx-auto">
      <div class="card-body items-center text-center gap-4">
        <h2 class="card-title text-2xl font-bold">
          {{ $t("loginmodal.pleaseAgree") }}
        </h2>
        <p class="text-center">
          {{
            $t(
              globalUser.loginModal.type === "login"
                ? "loginmodal.loginAgreement"
                : "loginmodal.registerAgreement"
            )
          }}
          <span
            @click="
              clickPolicy(isCN ? 'user-agreement-cn' : 'terms-of-service')
            "
            class="link link-primary"
          >
            {{ $t("loginmodal.userAgreement") }}
          </span>
          {{ $t("loginmodal.and") }}
          <span
            @click="clickPolicy(isCN ? 'privacy-policy-cn' : 'privacy-policy')"
            class="link link-primary"
          >
            {{ $t("loginmodal.privacyPolicy") }}
          </span>
        </p>

        <div class="card-actions justify-end mt-4">
          <button
            class="btn btn-primary btn-wide"
            @click="clickAgreeAndContinue"
          >
            {{ $t("loginmodal.agreeAndContinue") }}
          </button>
        </div>
      </div>
    </div>
  </VanPopup>

  <ModalPolicyPage v-model:show="showPolicy" :docType="policyType" />
</template>
<style>
.login-input.theme-white.input-field .van-field__body {
  background: white;
  @apply input-sm xs:input-md;
}

.login-input.theme-white.input-field input {
  color: black;
}
</style>
